import { useTheme } from '@mui/material/styles';
import useSiteConfig from '@common/hooks/useSiteConfig';

export const Logo = (props: { name: string | undefined }) => {
  const { data } = useSiteConfig();

  // If we don't have the siteconfig yet, just render empty. This'll be
  // handled by the LoadingScreen in `SiteConfigGuard` and will just render
  // an empty ring; since the load is synchronous (but with React useEffect
  // lag), it should be an order of milliseconds until we get the logo.
  if (!data) {
    return <></>;
  }

  if (data.themeOptions?.name === 'esports') {
    return <EsportLogo {...props} />;
  } else {
    return <DashLogo {...props} />;
  }
};

const DashLogo = (props: any) => {
  const theme = useTheme();
  const PRIMARY_MAIN = theme.palette.primary.main;

  return (
    <svg width="100%" height="100%" viewBox="0 0 143 75" xmlns="http://www.w3.org/2000/svg">
      <g fill={PRIMARY_MAIN} fillRule="evenodd" stroke="none" strokeWidth="1">
        <path
          d="M55.4165 5.92233C58.3865 2.18086 62.9021 0 67.679 0H105C125.954 0 142.941 16.9869 142.941 37.9412V37.9412H30L55.4165 5.92233Z"
          fill="url(#paint0_linear_57_10425)"
        />
        <path
          d="M9.99946 62.6859C5.99101 67.6222 9.5039 75 15.8627 75H105.882C126.349 75 142.941 58.4082 142.941 37.9412V37.9412H30.0929L9.99946 62.6859Z"
          fill="url(#paint1_linear_57_10425)"
        />
        <path
          d="M90.8823 0H105.441C126.152 0 142.941 16.7893 142.941 37.5V37.5C142.941 58.2107 126.152 75 105.441 75H90.8823V0Z"
          fill="url(#paint2_linear_57_10425)"
        />
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_57_10425"
          x1="30"
          y1="37.9412"
          x2="142.941"
          y2="37.9412"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#49A0BC" />
          <stop offset="1" stopColor="#64B6D1" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_57_10425"
          x1="36.8638"
          y1="46.8353"
          x2="129.399"
          y2="46.8353"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#87DCF9" />
          <stop offset="1" stopColor="#64B6D1" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_57_10425"
          x1="90.8823"
          y1="37.5"
          x2="132.756"
          y2="37.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#64B6D1" stopOpacity="0" />
          <stop offset="1" stopColor="#64B6D1" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const EsportLogo = (props: any) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.83837 5.33519C9.71837 5.33519 9.60837 5.41518 9.58837 5.53518L8.39837 11.0752L8.23837 11.8852L8.12837 12.3952L8.01837 12.9052L7.68837 14.3652C7.64837 14.5252 7.77837 14.6752 7.93837 14.6752H14.9284C15.0884 14.6752 15.2184 14.8252 15.1784 14.9852L14.9084 16.2452C14.8784 16.3652 14.7784 16.4452 14.6584 16.4452H4.80837C4.04837 16.4452 3.47837 16.2152 3.10837 15.7652C2.74837 15.2952 2.65837 14.6852 2.82837 13.9252L4.59837 5.57518C4.71837 4.98518 5.00837 4.50519 5.45837 4.12519C5.92837 3.75519 6.45837 3.56519 7.04837 3.56519H15.6384C16.2284 3.56519 16.6684 3.75519 16.9484 4.12519C17.2484 4.49519 17.3284 4.98518 17.2084 5.57518L15.7084 12.6952C15.6784 12.8152 15.5784 12.8952 15.4584 12.8952H9.33837C9.17837 12.8952 9.05837 12.7552 9.08837 12.5952L9.31837 11.2952C9.33837 11.1752 9.44837 11.0852 9.56837 11.0852H10.8784C10.9984 11.0852 11.1084 11.0052 11.1284 10.8852L12.2584 5.65518C12.2884 5.49518 12.1684 5.34519 12.0084 5.34519H9.83837V5.33519Z"
      fill="url(#paint0_linear_81_1864)"
    />
    <path
      d="M16.8284 18.5851C16.7984 18.7051 16.6984 18.7851 16.5784 18.7851H4.80836C2.94836 18.7851 1.87836 17.9551 1.29836 17.2551L1.24836 17.1851C0.698363 16.4751 0.138363 15.2351 0.538363 13.4151L2.30836 5.07509C2.53836 3.97509 3.11836 3.01509 3.96836 2.30509L3.99836 2.28509C4.87836 1.58509 5.92836 1.21509 7.04836 1.21509H15.6384C16.9584 1.21509 18.0784 1.73509 18.7984 2.68509C19.3084 3.33509 19.8284 4.44509 19.4884 6.04509L17.5584 15.2351H17.5384L16.8284 18.5851ZM5.17836 14.1051H5.34836L5.93836 11.4251L6.09836 10.5851L7.10836 5.90509H7.03836C7.03836 5.90509 6.97836 5.90509 6.92836 5.93509C6.90836 5.95509 6.89836 5.97509 6.87836 6.04509L5.17836 14.1051ZM13.5984 10.5551H13.7584L14.7384 5.90509H14.5984L13.5984 10.5551Z"
      fill="url(#paint1_linear_81_1864)"
    />
    <path
      d="M9.83837 5.33519C9.71837 5.33519 9.60837 5.41518 9.58837 5.53518L8.39837 11.0752L8.23837 11.8852L8.12837 12.3952L8.01837 12.9052L7.68837 14.3652C7.64837 14.5252 7.77837 14.6752 7.93837 14.6752H14.9284C15.0884 14.6752 15.2184 14.8252 15.1784 14.9852L14.9084 16.2452C14.8784 16.3652 14.7784 16.4452 14.6584 16.4452H4.80837C4.04837 16.4452 3.47837 16.2152 3.10837 15.7652C2.74837 15.2952 2.65837 14.6852 2.82837 13.9252L4.59837 5.57518C4.71837 4.98518 5.00837 4.50519 5.45837 4.12519C5.92837 3.75519 6.45837 3.56519 7.04837 3.56519H15.6384C16.2284 3.56519 16.6684 3.75519 16.9484 4.12519C17.2484 4.49519 17.3284 4.98518 17.2084 5.57518L15.7084 12.6952C15.6784 12.8152 15.5784 12.8952 15.4584 12.8952H9.33837C9.17837 12.8952 9.05837 12.7552 9.08837 12.5952L9.31837 11.2952C9.33837 11.1752 9.44837 11.0852 9.56837 11.0852H10.8784C10.9984 11.0852 11.1084 11.0052 11.1284 10.8852L12.2584 5.65518C12.2884 5.49518 12.1684 5.34519 12.0084 5.34519H9.83837V5.33519Z"
      fill="#171717"
    />
    <path
      d="M15.7884 17.2951C15.7584 17.4151 15.6584 17.4951 15.5384 17.4951H4.80836C3.44836 17.4951 2.68836 16.9151 2.28836 16.4351L2.26836 16.4051C1.70836 15.6851 1.55836 14.7651 1.79836 13.6851L3.56836 5.34513C3.73836 4.51513 4.15837 3.83513 4.78837 3.30513C5.45837 2.76513 6.21836 2.50513 7.04836 2.50513H15.6384C16.7984 2.50513 17.4384 3.03513 17.7784 3.47513C18.1184 3.90513 18.4684 4.65513 18.2284 5.78513L16.5084 13.9551H16.4884L15.7884 17.2951ZM3.91836 15.0951C3.91836 15.0951 3.92836 15.1151 3.93836 15.1151C4.01836 15.2051 4.22836 15.3851 4.80836 15.3851H6.18836C6.30836 15.3851 6.40836 15.3051 6.43836 15.1851L7.21836 11.6651L7.36836 10.8751L8.64836 4.92513C8.67836 4.76513 8.55836 4.61513 8.39836 4.61513H7.04836C6.69836 4.61513 6.39836 4.71513 6.11836 4.94513C5.86836 5.15513 5.70837 5.42513 5.62837 5.78513L3.85836 14.1451C3.71836 14.7551 3.85836 14.9951 3.91836 15.0951ZM12.3184 11.8451H14.5984C14.7184 11.8451 14.8284 11.7551 14.8484 11.6451L16.1684 5.35513C16.2484 4.95513 16.1484 4.82513 16.1184 4.77513L16.0984 4.75513C16.0984 4.75513 15.9984 4.61513 15.6384 4.61513H13.7684C13.6484 4.61513 13.5384 4.69513 13.5184 4.81513L12.0684 11.5251C12.0284 11.6951 12.1484 11.8451 12.3184 11.8451Z"
      fill="#010101"
    />
    <path
      d="M9.83837 5.33519C9.71837 5.33519 9.60837 5.41518 9.58837 5.53518L8.39837 11.0752L8.23837 11.8852L8.12837 12.3952L8.01837 12.9052L7.68837 14.3652C7.64837 14.5252 7.77837 14.6752 7.93837 14.6752H14.9284C15.0884 14.6752 15.2184 14.8252 15.1784 14.9852L14.9084 16.2452C14.8784 16.3652 14.7784 16.4452 14.6584 16.4452H4.80837C4.04837 16.4452 3.47837 16.2152 3.10837 15.7652C2.74837 15.2952 2.65837 14.6852 2.82837 13.9252L4.59837 5.57518C4.71837 4.98518 5.00837 4.50519 5.45837 4.12519C5.92837 3.75519 6.45837 3.56519 7.04837 3.56519H15.6384C16.2284 3.56519 16.6684 3.75519 16.9484 4.12519C17.2484 4.49519 17.3284 4.98518 17.2084 5.57518L15.7084 12.6952C15.6784 12.8152 15.5784 12.8952 15.4584 12.8952H9.33837C9.17837 12.8952 9.05837 12.7552 9.08837 12.5952L9.31837 11.2952C9.33837 11.1752 9.44837 11.0852 9.56837 11.0852H10.8784C10.9984 11.0852 11.1084 11.0052 11.1284 10.8852L12.2584 5.65518C12.2884 5.49518 12.1684 5.34519 12.0084 5.34519H9.83837V5.33519Z"
      fill="white"
    />
    <defs>
      <linearGradient
        id="paint0_linear_81_1864"
        x1="2.74837"
        y1="10.0052"
        x2="17.2584"
        y2="10.0052"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#74C15A" />
        <stop offset="1" stopColor="#74C15A" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_81_1864"
        x1="18.5084"
        y1="2.34509"
        x2="2.43836"
        y2="18.4151"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#74C15A" />
        <stop offset="1" stopColor="#74C15A" />
      </linearGradient>
    </defs>
  </svg>
);

export const logoHeight = '40px';
